import React, { useState, useEffect } from 'react';
import { Package, Plus, Trash2, Edit3, Save, X, Grid3X3, Layers, Box } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const WarehouseStructure = () => {
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRackModal, setShowAddRackModal] = useState(false);
  const [editingRack, setEditingRack] = useState(null);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [currentUserCompany, setCurrentUserCompany] = useState(null);
  const [activeCompanyTab, setActiveCompanyTab] = useState(null);
  const [newRack, setNewRack] = useState({
    name: '',
    shelves: 5,
    containers: 4,
    owner_company_id: ''
  });

  // Fetch real warehouse structure data
  useEffect(() => {
    fetchRacks();
    fetchAvailableCompanies();
    fetchCurrentUserCompany();
  }, []);

  const fetchRacks = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/structure/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const racksData = Array.isArray(response.data) ? response.data : 
                       response.data.results ? response.data.results : [];
      setRacks(racksData);
    } catch (error) {
      console.error('Error fetching warehouse structure:', error);
      // Show user-friendly error message
      alert('Error loading warehouse structure. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserCompany = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/current/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.data) {
        const companyData = {
          id: response.data.company,
          name: response.data.company_name
        };
        setCurrentUserCompany(companyData);
        // Save to localStorage for use in other components
        if (companyData.name) {
          localStorage.setItem('user_company_name', companyData.name);
        }
      }
    } catch (error) {
      console.error('Error fetching current user company:', error);
    }
  };

  const fetchAvailableCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/available_companies/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setAvailableCompanies(response.data || []);
    } catch (error) {
      console.error('Error fetching available companies:', error);
      // Fallback: use current user's company if available
      if (currentUserCompany) {
        setAvailableCompanies([{
          id: currentUserCompany.id,
          name: currentUserCompany.name,
          is_own: true
        }]);
      }
    }
  };

  const addRack = async () => {
    // Validation
    if (!newRack.name.trim()) {
      alert('Please enter a rack name');
      return;
    }

    if (newRack.name.trim().length > 15) {
      alert('Rack name must be 15 characters or less.\nCurrent length: ' + newRack.name.trim().length + ' characters\n\nTry shorter names like: A, B, C, A1, B1, RACK-1, WAREHOUSE-A, etc.');
      return;
    }

    if (newRack.shelves < 1 || newRack.shelves > 20) {
      alert('Number of shelves must be between 1 and 20');
      return;
    }

    if (newRack.containers < 1 || newRack.containers > 10) {
      alert('Number of containers must be between 1 and 10');
      return;
    }

    try {
      // Check if access token exists
      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        alert('You are not logged in. Please log in again.');
        // Redirect to login or refresh token
        window.location.href = '/login';
        return;
      }

      const rackData = {
        name: newRack.name.trim(),
        shelves: parseInt(newRack.shelves),
        containers: parseInt(newRack.containers),
        owner_company: newRack.owner_company_id || (currentUserCompany ? currentUserCompany.id : null)
      };

      await axios.post(`${API_BASE_URL}/api/warehouse/structure/`, rackData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Refresh the racks list
      await fetchRacks();

      setNewRack({ name: '', shelves: 5, containers: 4, owner_company_id: '' });
      setShowAddRackModal(false);
      alert('Rack created successfully!');
    } catch (error) {
      console.error('Error creating rack:', error);
      console.error('Full error response:', error.response?.data);

      if (error.response?.status === 403) {
        alert('Access denied. Please check if you are logged in and have permission to create racks.');
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      } else if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      } else if (error.response?.status === 400) {
        // Handle validation errors
        const errorData = error.response.data;
        let errorMessage = 'Validation error:\n\n';

        if (errorData.name) {
          console.log('Name errors:', errorData.name);
          errorMessage += `❌ Name: ${Array.isArray(errorData.name) ? errorData.name.join(', ') : errorData.name}\n`;
        }
        if (errorData.shelves) {
          errorMessage += `❌ Shelves: ${Array.isArray(errorData.shelves) ? errorData.shelves.join(', ') : errorData.shelves}\n`;
        }
        if (errorData.containers) {
          errorMessage += `❌ Containers: ${Array.isArray(errorData.containers) ? errorData.containers.join(', ') : errorData.containers}\n`;
        }
        if (errorData.detail) {
          errorMessage += `❌ ${errorData.detail}\n`;
        }
        if (errorData.non_field_errors) {
          errorMessage += `❌ ${Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors.join(', ') : errorData.non_field_errors}\n`;
        }

        // If no specific errors found, show all data
        if (!errorData.name && !errorData.shelves && !errorData.containers && !errorData.detail && !errorData.non_field_errors) {
          errorMessage += JSON.stringify(errorData, null, 2);
        }

        alert(errorMessage);
      } else {
        const errorMessage = error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Error creating rack. Please try again.';
        alert(errorMessage);
      }
    }
  };

  const startEditRack = (rack) => {
    setEditingRack({
      id: rack.id,
      name: rack.name,
      shelves: rack.shelves,
      containers: rack.containers,
      owner_company_id: rack.owner_company || ''
    });
  };

  const updateRack = async () => {
    // Validation
    if (!editingRack.name.trim()) {
      alert('Please enter a rack name');
      return;
    }

    if (editingRack.name.trim().length > 15) {
      alert('Rack name must be 15 characters or less.\nCurrent length: ' + editingRack.name.trim().length + ' characters');
      return;
    }

    if (editingRack.shelves < 1 || editingRack.shelves > 20) {
      alert('Number of shelves must be between 1 and 20');
      return;
    }

    if (editingRack.containers < 1 || editingRack.containers > 10) {
      alert('Number of containers must be between 1 and 10');
      return;
    }

    try {
      const rackData = {
        name: editingRack.name.trim(),
        shelves: parseInt(editingRack.shelves),
        containers: parseInt(editingRack.containers),
        owner_company: editingRack.owner_company_id || null
      };

      await axios.patch(`${API_BASE_URL}/api/warehouse/structure/${editingRack.id}/`, rackData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      // Refresh the racks list
      await fetchRacks();

      setEditingRack(null);
      alert('Rack updated successfully!');
    } catch (error) {
      console.error('Error updating rack:', error);
      console.error('Full error response:', error.response?.data);

      if (error.response?.status === 400) {
        const errorData = error.response.data;
        let errorMessage = 'Validation error:\n\n';

        if (errorData.name) {
          errorMessage += `❌ Name: ${Array.isArray(errorData.name) ? errorData.name.join(', ') : errorData.name}\n`;
        }
        if (errorData.shelves) {
          errorMessage += `❌ Shelves: ${Array.isArray(errorData.shelves) ? errorData.shelves.join(', ') : errorData.shelves}\n`;
        }
        if (errorData.containers) {
          errorMessage += `❌ Containers: ${Array.isArray(errorData.containers) ? errorData.containers.join(', ') : errorData.containers}\n`;
        }

        alert(errorMessage);
      } else {
        alert('Error updating rack. Please try again.');
      }
    }
  };

  const deleteRack = async (rackId) => {
    if (window.confirm('Are you sure you want to delete this rack? This will also delete all associated locations.')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/warehouse/structure/${rackId}/`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });

        // Refresh the racks list
        await fetchRacks();
        alert('Rack deleted successfully!');
      } catch (error) {
        console.error('Error deleting rack:', error);
        const errorMessage = error.response?.status === 400 
          ? 'Cannot delete rack. It may contain items that need to be moved first.'
          : 'Error deleting rack. Please try again.';
        alert(errorMessage);
      }
    }
  };

  const generateLocationCode = (rackName, shelf, container) => {
    const shelfStr = shelf.toString().padStart(2, '0');
    const containerStr = String.fromCharCode(65 + container); // A, B, C, D...
    return `${rackName}-${shelfStr}-${containerStr}`;
  };

  const RackCard = ({ rack }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
      name: rack.name,
      shelves: rack.shelves,
      containers: rack.containers
    });

    const handleSave = async () => {
      await updateRack(rack.id, {
        name: editData.name,
        shelves: parseInt(editData.shelves),
        containers: parseInt(editData.containers)
      });
      setIsEditing(false);
    };

    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
        <div className="flex items-center justify-between mb-4">
          {isEditing ? (
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData({...editData, name: e.target.value})}
              className="text-2xl font-bold text-slate-900 border-b-2 border-brand-500 bg-transparent outline-none"
            />
          ) : (
            <h3 className="text-2xl font-bold text-slate-900">Rack {rack.name}</h3>
          )}
          
          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="p-2 text-green-600 hover:bg-green-50 rounded"
                >
                  <Save className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="p-2 text-slate-600 hover:bg-steel-50 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => startEditRack(rack)}
                  className="p-2 text-brand-600 hover:bg-brand-50 rounded"
                  title="Edit rack"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => deleteRack(rack.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center">
            <Layers className="h-5 w-5 text-brand-600 mr-2" />
            {isEditing ? (
              <div>
                <span className="text-sm text-slate-600">Shelves: </span>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editData.shelves}
                  onChange={(e) => setEditData({...editData, shelves: e.target.value})}
                  className="w-16 px-1 border border-brand-200 rounded text-center"
                />
              </div>
            ) : (
              <span className="text-slate-700">{rack.shelves} Shelves</span>
            )}
          </div>
          
          <div className="flex items-center">
            <Box className="h-5 w-5 text-green-600 mr-2" />
            {isEditing ? (
              <div>
                <span className="text-sm text-slate-600">Containers: </span>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={editData.containers}
                  onChange={(e) => setEditData({...editData, containers: e.target.value})}
                  className="w-16 px-1 border border-brand-200 rounded text-center"
                />
              </div>
            ) : (
              <span className="text-slate-700">{rack.containers} per shelf</span>
            )}
          </div>
        </div>

        <div className="text-sm text-slate-500 mb-4">
          Total locations: {rack.shelves * rack.containers} | Items stored: {rack.items || 0}
        </div>

        {/* Rack Preview */}
        <div className="border border-brand-200 rounded-xl p-3 bg-steel-50">
          <h4 className="text-sm font-medium text-slate-700 mb-2">Rack Layout Preview</h4>
          <div className="space-y-1">
            {Array.from({ length: rack.shelves }, (_, shelfIndex) => (
              <div key={shelfIndex} className="flex items-center space-x-1">
                <span className="text-xs text-slate-500 w-8">
                  {(shelfIndex + 1).toString().padStart(2, '0')}:
                </span>
                {Array.from({ length: rack.containers }, (_, containerIndex) => (
                  <div
                    key={containerIndex}
                    className="w-8 h-6 bg-white border border-brand-200 rounded flex items-center justify-center text-xs font-mono"
                    title={generateLocationCode(rack.name, shelfIndex + 1, containerIndex)}
                  >
                    {String.fromCharCode(65 + containerIndex)}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Example location: {generateLocationCode(rack.name, 1, 0)}
          </div>
        </div>
      </div>
    );
  };

  // Group racks by company
  const racksByCompany = racks.reduce((acc, rack) => {
    const companyName = rack.owner_company_name || (currentUserCompany?.name || 'My Company');
    if (!acc[companyName]) {
      acc[companyName] = [];
    }
    acc[companyName].push(rack);
    return acc;
  }, {});

  const companies = Object.keys(racksByCompany);

  // Set initial active tab when companies are loaded
  useEffect(() => {
    if (companies.length > 0 && !activeCompanyTab && currentUserCompany) {
      // Prioritize current user's company
      const myCompany = companies.find(c => c === currentUserCompany?.name);
      setActiveCompanyTab(myCompany || companies[0]);
    }
  }, [racks, activeCompanyTab, currentUserCompany]);

  if (loading) {
    return (
      <div className="p-6 bg-steel-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-steel-300 rounded w-1/4 mb-4"></div>
            <div className="h-4 bg-steel-300 rounded w-1/2 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-steel-300 rounded"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-64 bg-steel-300 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Get active company's racks
  const activeCompanyRacks = activeCompanyTab ? racksByCompany[activeCompanyTab] || [] : [];
  const activeCompanyTotalRacks = activeCompanyRacks.length;
  const activeCompanyTotalShelves = activeCompanyRacks.reduce((sum, rack) => sum + rack.shelves, 0);
  const activeCompanyTotalLocations = activeCompanyRacks.reduce((sum, rack) => sum + (rack.shelves * rack.containers), 0);
  const activeCompanyTotalItems = activeCompanyRacks.reduce((sum, rack) => sum + (rack.items || 0), 0);

  return (
    <div className="p-6 bg-steel-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Warehouse Structure
              </h1>
              <p className="text-slate-600 mt-2">Configure your warehouse layout with racks, shelves, and containers</p>
            </div>
            <button
              onClick={() => setShowAddRackModal(true)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors shadow-lg shadow-brand-500/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Rack
            </button>
          </div>
        </div>

        {/* Company Tabs */}
        {companies.length > 0 && (
          <div className="mb-6">
            <div className="border-b border-brand-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {companies.map((companyName) => (
                  <button
                    key={companyName}
                    onClick={() => setActiveCompanyTab(companyName)}
                    className={`${
                      activeCompanyTab === companyName
                        ? 'border-brand-500 text-brand-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-brand-200'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
                  >
                    {companyName}
                    <span className="ml-2 py-0.5 px-2 rounded-full text-xs bg-steel-100">
                      {racksByCompany[companyName].length}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Active Company Summary Stats */}
        {activeCompanyTab && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Racks</p>
                  <p className="text-3xl font-bold text-slate-900">{activeCompanyTotalRacks}</p>
                </div>
                <div className="h-12 w-12 bg-brand-100 rounded-xl flex items-center justify-center">
                  <Grid3X3 className="h-6 w-6 text-brand-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Shelves</p>
                  <p className="text-3xl font-bold text-slate-900">{activeCompanyTotalShelves}</p>
                </div>
                <div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Layers className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Locations</p>
                  <p className="text-3xl font-bold text-slate-900">{activeCompanyTotalLocations}</p>
                </div>
                <div className="h-12 w-12 bg-brand-100 rounded-xl flex items-center justify-center">
                  <Box className="h-6 w-6 text-brand-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-brand-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Items</p>
                  <p className="text-3xl font-bold text-slate-900">{activeCompanyTotalItems}</p>
                </div>
                <div className="h-12 w-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Package className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Racks Grid - Active Company Only */}
        {companies.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-brand-200">
            <Grid3X3 className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No racks configured</h3>
            <p className="mt-1 text-sm text-slate-500">
              Start by adding your first rack to organize your warehouse.
            </p>
            <button
              onClick={() => setShowAddRackModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-brand-600 hover:bg-brand-700 shadow-lg shadow-brand-500/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Rack
            </button>
          </div>
        ) : activeCompanyTab && activeCompanyRacks.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {activeCompanyRacks.map(rack => (
              <RackCard key={rack.id} rack={rack} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-brand-200">
            <Grid3X3 className="mx-auto h-12 w-12 text-slate-400" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No racks for {activeCompanyTab}</h3>
            <p className="mt-1 text-sm text-slate-500">
              Add a rack to start organizing this company's warehouse.
            </p>
          </div>
        )}

        {/* Add Rack Modal */}
        {showAddRackModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-xl bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Add New Rack</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Rack Name (max 15 characters)
                    </label>
                    <input
                      type="text"
                      value={newRack.name}
                      onChange={(e) => setNewRack({...newRack, name: e.target.value})}
                      placeholder="e.g., A, B, RACK-1, WAREHOUSE-A..."
                      maxLength="15"
                      className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                        newRack.name.length > 15 ? 'border-red-500' : 'border-brand-200'
                      }`}
                      required
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        Examples: A, B, RACK-1, WAREHOUSE-A
                      </span>
                      <span className={`text-xs ${newRack.name.length > 15 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                        {newRack.name.length}/15
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Number of Shelves
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={newRack.shelves}
                      onChange={(e) => setNewRack({...newRack, shelves: e.target.value})}
                      className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Containers per Shelf
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={newRack.containers}
                      onChange={(e) => setNewRack({...newRack, containers: e.target.value})}
                      className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Owner Company
                    </label>
                    <select
                      value={newRack.owner_company_id}
                      onChange={(e) => setNewRack({...newRack, owner_company_id: e.target.value})}
                      className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">Select company...</option>
                      {availableCompanies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name} {company.is_own ? '(Your Company)' : '(Partner)'}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Choose which company owns this rack
                    </p>
                  </div>

                  <div className="bg-steel-50 p-3 rounded-xl">
                    <p className="text-sm text-slate-600">
                      This will create <strong>{newRack.shelves * newRack.containers}</strong> storage locations
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Example location: {newRack.name}-01-A
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={addRack}
                    disabled={!newRack.name.trim()}
                    className="flex-1 bg-brand-600 text-white py-2 px-4 rounded-xl hover:bg-brand-700 disabled:bg-steel-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500 shadow-lg shadow-brand-500/20"
                  >
                    Add Rack
                  </button>
                  <button
                    onClick={() => {
                      setShowAddRackModal(false);
                      setNewRack({ name: '', shelves: 5, containers: 4 });
                    }}
                    className="flex-1 bg-steel-300 text-slate-700 py-2 px-4 rounded-xl hover:bg-steel-400 focus:outline-none focus:ring-2 focus:ring-steel-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Rack Modal */}
        {editingRack && (
          <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-xl bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-slate-900 mb-4">Edit Rack</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Rack Name (max 15 characters)
                    </label>
                    <input
                      type="text"
                      value={editingRack.name}
                      onChange={(e) => setEditingRack({...editingRack, name: e.target.value})}
                      placeholder="e.g., A, B, RACK-1, WAREHOUSE-A..."
                      maxLength="15"
                      className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                        editingRack.name.length > 15 ? 'border-red-500' : 'border-brand-200'
                      }`}
                      required
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-xs text-slate-500">
                        Examples: A, B, RACK-1, WAREHOUSE-A
                      </span>
                      <span className={`text-xs ${editingRack.name.length > 15 ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                        {editingRack.name.length}/15
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Number of Shelves
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={editingRack.shelves}
                      onChange={(e) => setEditingRack({...editingRack, shelves: e.target.value})}
                      className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Containers per Shelf
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={editingRack.containers}
                      onChange={(e) => setEditingRack({...editingRack, containers: e.target.value})}
                      className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Owner Company
                    </label>
                    <select
                      value={editingRack.owner_company_id}
                      onChange={(e) => setEditingRack({...editingRack, owner_company_id: e.target.value})}
                      className="w-full px-3 py-2 border border-brand-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">Select company...</option>
                      {availableCompanies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name} {company.is_own ? '(Your Company)' : '(Partner)'}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Change which company owns this rack
                    </p>
                  </div>

                  <div className="bg-steel-50 p-3 rounded-xl">
                    <p className="text-sm text-slate-600">
                      This rack has <strong>{editingRack.shelves * editingRack.containers}</strong> storage locations
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Example location: {editingRack.name}-01-A
                    </p>
                  </div>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={updateRack}
                    disabled={!editingRack.name.trim()}
                    className="flex-1 bg-brand-600 text-white py-2 px-4 rounded-xl hover:bg-brand-700 disabled:bg-steel-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brand-500 shadow-lg shadow-brand-500/20"
                  >
                    Update Rack
                  </button>
                  <button
                    onClick={() => setEditingRack(null)}
                    className="flex-1 bg-steel-300 text-slate-700 py-2 px-4 rounded-xl hover:bg-steel-400 focus:outline-none focus:ring-2 focus:ring-steel-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseStructure;